import * as THREE from "three";
import { defineModel, spin, type Part } from "../../lib";

// Wizard Tower — a plump round stone tower wearing a SWOLLEN deep-blue cone
// hat tipped with a golden star, a tiny railed balcony over the too-big door,
// and a magic orb that slowly circles the tower like a tame comet. The orb
// ("orb") rides a full-turn rotation about the tower axis on the idle loop;
// the star ("star") twinkles with a scale pulse.
//
// Frame: y = 0 is the ground, the door faces +Z. 1 unit = 1 m; the tower is a
// squat ~6 m caricature on a 2x2 tile plot.

const STONE = "#b0a08c";       // warm tower stone
const STONE_DARK = "#8f8171";  // base skirt / shading
const ROOF = "#31479e";        // deep saturated blue cone
const ROOF_DARK = "#25366f";   // roof underside ring
const TRIM = "#f6efe3";        // cream trim band and window frames
const DOOR = "#8a5a33";        // big wooden door
const DOOR_DARK = "#6b4426";   // door recess
const GLASS = "#ffd98a";       // warm lit windows
const STAR = "#f0c33c";        // golden tip star
const ORB = "#d46ef2";         // the circling magic orb

// ── frame heights ────────────────────────────────────────────────────────────
const TOWER_H = 3.6;                 // stone shaft, ground to roof seat
const ROOF_Y = TOWER_H + 0.05;       // where the swollen cone seats
const ROOF_H = 2.0;                  // the cone crown
const STAR_Y = ROOF_Y + ROOF_H + 0.22; // the golden star rides the tip

// The orb circles the tower at shoulder height, out past the walls. It is a
// part offset from the axis; the idle track spins it about [0, 0, 0] (the
// tower axis), so the offset becomes the orbit radius.
const ORB_AT: [number, number, number] = [1.95, 2.6, 0];

// A tiny arched window: a cream frame with a warm pane, pressed into the
// tower wall along direction `az` (radians from +Z) at height `y`.
function archWindow(az: number, y: number): Part[] {
  // Tower surface radius at window height, plus a nudge to stand proud.
  const wallR = 1.5 - (y / TOWER_H) * 0.35 + 0.03;
  const x = Math.sin(az) * wallR;
  const z = Math.cos(az) * wallR;
  const rot: [number, number, number] = [Math.PI / 2, az, 0];
  return [
    { geometry: new THREE.CylinderGeometry(0.2, 0.2, 0.14, 10), color: TRIM,
      position: [x, y, z], rotation: rot, eulerOrder: "YXZ" },
    { geometry: new THREE.CylinderGeometry(0.13, 0.13, 0.18, 10), color: GLASS,
      position: [x, y, z], rotation: rot, eulerOrder: "YXZ" },
  ];
}

// The golden star: two crossed flattened boxes in the front plane plus the
// same pair turned 90 degrees, so it reads as a star from every side. All
// named "star" so they twinkle as one.
function star(): Part[] {
  const size: [number, number, number] = [0.55, 0.55, 0.12];
  const out: Part[] = [];
  for (const yaw of [0, Math.PI / 2]) {
    out.push(
      { geometry: new THREE.BoxGeometry(...size), color: STAR,
        position: [0, STAR_Y, 0], rotation: [0, yaw, 0], name: "star" },
      { geometry: new THREE.BoxGeometry(...size), color: STAR,
        position: [0, STAR_Y, 0], rotation: [0, yaw, Math.PI / 4],
        eulerOrder: "YXZ", name: "star" },
    );
  }
  return out;
}

export const wizardTower = defineModel({
  id: "wizard-tower",
  name: "Wizard Tower",
  batchId: "mess",
  tags: ["mess", "cute", "animated", "building", "fantasy", "wizard", "tower", "magic"],
  summary: "A plump stone tower with a deep-blue cone hat, a golden tip star and an orbiting magic orb.",
  description:
    "A round storybook wizard tower: warm stone walls under a swollen " +
    "deep-blue cone roof tipped with a golden star, a tiny railed balcony over " +
    "the too-big door and arched windows glowing warm. A magic orb slowly " +
    "circles the tower on the idle loop while the star twinkles.",
  author: "models",
  footprint: 2.1,
  tiles: { w: 2, h: 2 },
  parts(): Part[] {
    return [
      // ── the stone shaft ───────────────────────────────────────────────────
      // A plump tapered drum, fatter at the base so it reads planted.
      { geometry: new THREE.CylinderGeometry(1.15, 1.5, TOWER_H, 16), color: STONE,
        position: [0, TOWER_H / 2, 0] },
      // A shaded skirt at the base.
      { geometry: new THREE.CylinderGeometry(1.55, 1.65, 0.3, 16), color: STONE_DARK,
        position: [0, 0.15, 0] },
      // Cream trim band under the roof.
      { geometry: new THREE.CylinderGeometry(1.22, 1.18, 0.24, 16), color: TRIM,
        position: [0, TOWER_H - 0.05, 0] },

      // ── the swollen deep-blue cone hat ────────────────────────────────────
      // Dark underside ring, then the big overhanging cone.
      { geometry: new THREE.CylinderGeometry(1.7, 1.35, 0.16, 16), color: ROOF_DARK,
        position: [0, ROOF_Y + 0.04, 0] },
      { geometry: new THREE.ConeGeometry(1.75, ROOF_H, 16), color: ROOF,
        position: [0, ROOF_Y + ROOF_H / 2 + 0.1, 0] },
      // The golden star riding the tip.
      ...star(),

      // ── the too-big door (facing +Z) ──────────────────────────────────────
      { geometry: new THREE.BoxGeometry(1.0, 1.6, 0.14), color: DOOR_DARK,
        position: [0, 0.8, 1.34] },
      { geometry: new THREE.BoxGeometry(0.84, 1.46, 0.16), color: DOOR,
        position: [0, 0.73, 1.38] },
      // Round-top arch over the door slab.
      { geometry: new THREE.CylinderGeometry(0.42, 0.42, 0.16, 12), color: DOOR,
        position: [0, 1.46, 1.38], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.SphereGeometry(0.06, 8, 6), color: STAR,
        position: [0.3, 0.8, 1.48] },
      // A chunky stone step.
      { geometry: new THREE.BoxGeometry(1.1, 0.16, 0.5), color: STONE_DARK,
        position: [0, 0.08, 1.62] },

      // ── the tiny balcony over the door ────────────────────────────────────
      // A basket-style juliet balcony: a plump half-sunk tub jutting from the
      // front wall with a cream rim, under a wee arched balcony doorway.
      { geometry: new THREE.CylinderGeometry(0.52, 0.36, 0.55, 12), color: STONE_DARK,
        position: [0, 2.32, 1.3] },
      { geometry: new THREE.CylinderGeometry(0.55, 0.55, 0.12, 12), color: TRIM,
        position: [0, 2.62, 1.3] },
      { geometry: new THREE.BoxGeometry(0.5, 0.6, 0.16), color: DOOR_DARK,
        position: [0, 2.8, 1.18] },
      { geometry: new THREE.CylinderGeometry(0.25, 0.25, 0.16, 12), color: DOOR_DARK,
        position: [0, 3.1, 1.18], rotation: [Math.PI / 2, 0, 0] },

      // ── arched windows around the shaft ───────────────────────────────────
      ...archWindow(Math.PI / 2.4, 1.9),      // right shoulder
      ...archWindow(-Math.PI / 2.4, 1.9),     // left shoulder
      ...archWindow(Math.PI, 2.6),            // back, up high

      // ── the magic orb (named so the idle loop can orbit it) ───────────────
      { geometry: new THREE.SphereGeometry(0.3, 12, 10), color: ORB,
        position: ORB_AT, name: "orb" },
      // A little sparkle trailing the orb.
      { geometry: new THREE.SphereGeometry(0.12, 8, 6), color: "#eec2f9",
        position: [ORB_AT[0] + 0.1, ORB_AT[1] + 0.28, ORB_AT[2] - 0.42], name: "orb" },
    ];
  },
  animations: [
    // Idle — the magic orb slowly circles the tower, a full turn about the
    // tower axis; the offset part becomes the orbit.
    {
      id: "idle",
      label: "Orb orbit",
      duration: 8,
      loop: true,
      tracks: [spin("orb", [0, 0, 0], { axis: "y", duration: 8 })],
    },
    // Twinkle — the signature move: the golden star swells and settles in
    // little sparkling beats.
    {
      id: "twinkle",
      label: "Star twinkle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "star",
          pivot: [0, STAR_Y, 0],
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 0.8, value: 1.35, ease: "sine" },
            { t: 1.6, value: 1, ease: "sine" },
            { t: 2.2, value: 1.2, ease: "sine" },
            { t: 3.2, value: 1, ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, Math.PI / 4], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
